import React, { useState } from 'react';
import { Button, Descriptions, Input, Modal, Radio, Space } from 'antd';
import dayjs from 'dayjs';
import type { ExpV2DecisionBody, ExpV2DecisionKind, ExpV2Test } from '@/types/experimentsV2';
import { CopyableId } from '@/components/CopyableId';

interface Props {
  test: ExpV2Test;
  canEdit: boolean;
  onSave: (body: ExpV2DecisionBody) => void | Promise<void>;
}

const DECISION_LABEL: Record<ExpV2DecisionKind, string> = {
  ship: 'Ship',
  kill: 'Kill',
  iterate: 'Iterate',
};

/** What the team decided once the test ended: the record readers check before reusing a key. */
export const DecisionForm: React.FC<Props> = ({ test, canEdit, onSave }) => {
  const d = test.decision;
  const [decision, setDecision] = useState<ExpV2DecisionKind>(d?.decision ?? 'ship');
  const [reason, setReason] = useState(d?.reason ?? '');
  const [ticket, setTicket] = useState(d?.cleanupTicket ?? '');

  const save = () =>
    Modal.confirm({
      title: `Record "${DECISION_LABEL[decision]}" for ${test.key}?`,
      onOk: () =>
        onSave({
          decision,
          reason: reason.trim(),
          ...(ticket.trim() ? { cleanupTicket: ticket.trim() } : {}),
        }),
    });

  return (
    <Space direction="vertical" style={{ width: '100%' }} data-section="decision">
      {d && (
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Decision">{DECISION_LABEL[d.decision]}</Descriptions.Item>
          <Descriptions.Item label="Reason">{d.reason}</Descriptions.Item>
          {d.cleanupTicket && (
            <Descriptions.Item label="Clean-up ticket">{d.cleanupTicket}</Descriptions.Item>
          )}
          <Descriptions.Item label="Decided">
            <CopyableId value={d.decidedBy} /> {dayjs(d.decidedAt).format('D MMM YYYY HH:mm')}
          </Descriptions.Item>
        </Descriptions>
      )}
      {canEdit && (
        <>
          <Radio.Group value={decision} onChange={(e) => setDecision(e.target.value)}>
            {(Object.keys(DECISION_LABEL) as ExpV2DecisionKind[]).map((k) => (
              <Radio key={k} value={k}>
                {DECISION_LABEL[k]}
              </Radio>
            ))}
          </Radio.Group>
          <Input.TextArea
            id="expv2-decision-reason"
            rows={2}
            placeholder="Why (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Input
            id="expv2-decision-ticket"
            placeholder="Clean-up ticket (optional)"
            value={ticket}
            onChange={(e) => setTicket(e.target.value)}
          />
          <Button type="primary" disabled={reason.trim() === ''} onClick={save}>
            {d ? 'Change decision' : 'Record decision'}
          </Button>
        </>
      )}
    </Space>
  );
};
